import { useRef } from 'react';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem, MenuItemCommandEvent } from 'primereact/menuitem';
import { HonorariumLiquidationProfessionalDTO } from '../../dto/HonorariumLiquidationDTO';

interface LiquidationProfessionalRowActionsProps {
    professional: HonorariumLiquidationProfessionalDTO;
    onRegisterPayment: (professional: HonorariumLiquidationProfessionalDTO) => void;
    onViewForms: (professional: HonorariumLiquidationProfessionalDTO) => void;
    onDownloadSummary: (professional: HonorariumLiquidationProfessionalDTO) => void;
    onViewDetail: (professional: HonorariumLiquidationProfessionalDTO) => void;
}

/**
 * Acciones de un profesional en una liquidación cerrada: menú ⋮ con ítems neutros (DS §3.1). Registrar el pago
 * desde acá evita entrar al detalle del profesional; abre el mismo diálogo. El resumen PDF existe aunque se haya
 * saldado sin orden de pago.
 */
export default function LiquidationProfessionalRowActions({
    professional,
    onRegisterPayment,
    onViewForms,
    onDownloadSummary,
    onViewDetail,
}: LiquidationProfessionalRowActionsProps) {
    const menuRef = useRef<Menu>(null);

    // La fila entera lleva al detalle: los clicks del menú no se propagan a ella (en React, un portal también propaga)
    const run = (action: (p: HonorariumLiquidationProfessionalDTO) => void) => (e: MenuItemCommandEvent) => {
        e.originalEvent.stopPropagation();
        action(professional);
    };

    const items: MenuItem[] = [
        ...(professional.pendingAmount != null && professional.pendingAmount > 0
            ? [{ label: 'Registrar pago', icon: 'pi pi-wallet', command: run(onRegisterPayment) }]
            : []),
        ...(professional.internalReceiptForm != null
            ? [
                { label: 'Ver comprobantes', icon: 'pi pi-file', command: run(onViewForms) },
                { label: 'Descargar resumen', icon: 'pi pi-download', command: run(onDownloadSummary) },
            ]
            : []),
        { label: 'Ver detalle', icon: 'pi pi-eye', command: run(onViewDetail) },
    ];

    return (
        <>
            <Menu model={items} popup ref={menuRef} className="md-menu" />
            <Button
                text
                rounded
                icon="pi pi-ellipsis-v"
                className="md-icon-btn"
                aria-label={`Acciones de ${professional.professionalName}`}
                aria-haspopup
                tooltip="Acciones"
                tooltipOptions={{ position: 'left' }}
                onClick={event => {
                    event.stopPropagation();
                    menuRef.current?.toggle(event);
                }}
            />
        </>
    );
}
